import { useState, type ReactNode, useMemo } from 'react';
import { Redirect, Route, Switch } from 'wouter';
import { Toaster } from 'sonner';
import ErrorBoundary from '@/components/ErrorBoundary';
import DashboardLayout from '@/components/layouts/DashboardLayout';
import CrmLayout from '@/components/layouts/CrmLayout';
import { AuthProvider } from '@/contexts/AuthContext';
import { useAuth } from '@/hooks/useAuth';
import { resolveApiBaseUrl } from '@/lib/apiBaseUrl';
import { createApiClientAuthAdapter } from '@/lib/createApiClientAuthAdapter';
import LoginPage from '@/pages/Login';
import DashboardPage from '@/pages/Dashboard';
import NotFoundPage from '@/pages/NotFound';
import LabLayout from '@/components/layouts/LabLayout';
import CellBatchesListPage from '@/pages/lab/CellBatchesListPage';
import CellBatchDetailPage from '@/pages/lab/CellBatchDetailPage';
import ExceptionsListPage from '@/pages/lab/ExceptionsListPage';
import ExceptionEventDetailPage from '@/pages/lab/ExceptionEventDetailPage';
import QcOrdersListPage from '@/pages/lab/QcOrdersListPage';
import QcOrderDetailPage from '@/pages/lab/QcOrderDetailPage';
import ReleaseOrdersListPage from '@/pages/lab/ReleaseOrdersListPage';
import ReleaseOrderDetailPage from '@/pages/lab/ReleaseOrderDetailPage';
import InfusionOrdersListPage from '@/pages/lab/InfusionOrdersListPage';
import InfusionOrderDetailPage from '@/pages/lab/InfusionOrderDetailPage';
import InfusionIdentityVerifyPage from '@/pages/lab/InfusionIdentityVerifyPage';
import SampleRecordsListPage from '@/pages/lab/SampleRecordsListPage';
import SampleRecordDetailPage from '@/pages/lab/SampleRecordDetailPage';
import SampleRecordCreatePage from '@/pages/lab/SampleRecordCreatePage';
import LabTraceabilityPage from '@/pages/lab/LabTraceabilityPage';
// Risk-event read-model page — Issue #400
import RiskReadModelPage from '@/pages/risk/RiskReadModelPage';
// Operations pages
import WorkOrderListPage from '@/pages/operations/WorkOrderListPage';
import WorkOrderDetailPage from '@/pages/operations/WorkOrderDetailPage';
import WorkOrderCreatePage from '@/pages/operations/WorkOrderCreatePage';
// CRM pages
import CustomerListPage from '@/pages/crm/CustomerListPage';
import { OrderList } from '@/pages/crm/OrderList';
import { OrderDetail } from '@/pages/crm/OrderDetail';
import { CreateOrder } from '@/pages/crm/CreateOrder';
import CustomerDetailPage from '@/pages/crm/CustomerDetailPage';
import CustomerCreatePage from '@/pages/crm/CustomerCreatePage';
import ChannelPartnerListPage from '@/pages/crm/ChannelPartnerListPage';
import ChannelPartnerDetailPage from '@/pages/crm/ChannelPartnerDetailPage';
import ChannelPartnerCreatePage from '@/pages/crm/ChannelPartnerCreatePage';
// Channel ROI pages
import ChannelRoiListPage from '@/pages/channel-roi/ChannelRoiListPage';
import ChannelRoiDetailPage from '@/pages/channel-roi/ChannelRoiDetailPage';
// Scheduling pages
import SchedulingLayout from '@/components/layouts/SchedulingLayout';
// Proposal pages
import ProposalLayout from '@/components/layouts/ProposalLayout';
import ProposalListPage from '@/pages/proposal/ProposalListPage';
import ProposalCreatePage from '@/pages/proposal/ProposalCreatePage';
import ProposalDetailPage from '@/pages/proposal/ProposalDetailPage';
import AiProposalReviewDashboardPage from '@/pages/proposal/AiProposalReviewDashboardPage';
import AppointmentListPage from '@/pages/scheduling/AppointmentListPage';
import AppointmentCreatePage from '@/pages/scheduling/AppointmentCreatePage';
import AppointmentDetailPage from '@/pages/scheduling/AppointmentDetailPage';
import StaffSchedulesPage from '@/pages/scheduling/StaffSchedulesPage';
// Finance pages — Issue #134
import FinanceLayout from '@/components/layouts/FinanceLayout';
import PaymentRecordsPage from '@/pages/finance/PaymentRecordsPage';
import ProjectUnlocksPage from '@/pages/finance/ProjectUnlocksPage';
import RevenueLedgersPage from '@/pages/finance/RevenueLedgersPage';
import CommissionLedgersPage from '@/pages/finance/CommissionLedgersPage';
// Finance pages — Issue #329 (invoice / installment / adjustment)
import InvoicesPage from '@/pages/finance/InvoicesPage';
import InstallmentsPage from '@/pages/finance/InstallmentsPage';
import AdjustmentsPage from '@/pages/finance/AdjustmentsPage';
// Finance pages — Issue #368 (template base)
import FinanceTemplatesPage from '@/pages/finance/FinanceTemplatesPage';
import FinanceCounterpartiesPage from '@/pages/finance/FinanceCounterpartiesPage';
import PaymentApplicationsPage from '@/pages/finance/PaymentApplicationsPage';
import EmployeeAdvancesPage from '@/pages/finance/EmployeeAdvancesPage';
import ExpenseReimbursementsPage from '@/pages/finance/ExpenseReimbursementsPage';
// Finance pages — Issue #371 (payroll batches)
import PayrollBatchesPage from '@/pages/finance/PayrollBatchesPage';
import PayrollBatchDetailPage from '@/pages/finance/PayrollBatchDetailPage';
// Finance pages — Issue #372 (multi-stage commission rules / details /
// payout candidates / wide-template export)
import CommissionRulesPage from '@/pages/finance/CommissionRulesPage';
import CommissionDetailsPage from '@/pages/finance/CommissionDetailsPage';
import CommissionPayoutCandidatesPage from '@/pages/finance/CommissionPayoutCandidatesPage';
import CommissionWideSheetExportPage from '@/pages/finance/CommissionWideSheetExportPage';
// Finance pages — Issue #373 (sales management standard report)
import SalesReportPage from '@/pages/finance/SalesReportPage';
// Finance pages — Issue #402 (unified finance audit view)
import FinanceAuditViewPage from '@/pages/finance/FinanceAuditViewPage';
import ComplianceLayout from '@/components/layouts/ComplianceLayout';
import ComplianceHomePage from '@/pages/compliance/ComplianceHomePage';
import PolicyFilesPage from '@/pages/compliance/PolicyFilesPage';
import ProjectStatusesPage from '@/pages/compliance/ProjectStatusesPage';
import PromotionReviewPage from '@/pages/compliance/PromotionReviewPage';
import SensitiveTermsPage from '@/pages/compliance/SensitiveTermsPage';
import RecheckQueuePage from '@/pages/compliance/RecheckQueuePage';
import TaskCenterPage from '@/pages/task-center/TaskCenterPage';
import CustomerDocumentReviewPage from '@/pages/customer-documents/CustomerDocumentReviewPage';
import HealthReportPublicationReviewPage from '@/pages/health-report-publications/HealthReportPublicationReviewPage';
import ComplaintListPage from '@/pages/complaints/ComplaintListPage';
import ComplaintDetailPage from '@/pages/complaints/ComplaintDetailPage';
import ComplaintCreatePage from '@/pages/complaints/ComplaintCreatePage';
// HR pages — Issue #176
import EmployeeListPage from '@/pages/hr/EmployeeListPage';
import ImportListPage from '@/pages/hr/ImportListPage';
import ImportCreatePage from '@/pages/hr/ImportCreatePage';
import ImportDetailPage from '@/pages/hr/ImportDetailPage';
// HR pages — Issue #188 (attendance / recruitment / training)
import AttendanceSyncListPage from '@/pages/hr/AttendanceSyncListPage';
import CandidateListPage from '@/pages/hr/CandidateListPage';
import CandidateDetailPage from '@/pages/hr/CandidateDetailPage';
import InterviewTemplateListPage from '@/pages/hr/InterviewTemplateListPage';
import InterviewScheduleListPage from '@/pages/hr/InterviewScheduleListPage';
import TrainingRecordListPage from '@/pages/hr/TrainingRecordListPage';
// HR pages — Issue #362 (credential / certificate expiry + login freeze)
import CertificateListPage from '@/pages/hr/CertificateListPage';
import CertificateCreatePage from '@/pages/hr/CertificateCreatePage';
import CertificateDetailPage from '@/pages/hr/CertificateDetailPage';
import OffboardingListPage from '@/pages/hr/OffboardingListPage';
import OffboardingCreatePage from '@/pages/hr/OffboardingCreatePage';
import OffboardingDetailPage from '@/pages/hr/OffboardingDetailPage';
// Admin fixed assets — Issue #364
import AdminAssetListPage from '@/pages/admin-assets/AdminAssetListPage';
import AdminAssetCreatePage from '@/pages/admin-assets/AdminAssetCreatePage';
import AdminAssetDetailPage from '@/pages/admin-assets/AdminAssetDetailPage';
// Admin affairs — Issue #365 (meeting rooms / bookings / vehicle / seal / announcements)
import AdminAffairsHubPage from '@/pages/admin-affairs/AdminAffairsHubPage';
import MeetingRoomListPage from '@/pages/admin-affairs/MeetingRoomListPage';
import MeetingBookingListPage from '@/pages/admin-affairs/MeetingBookingListPage';
import VehicleRequestListPage from '@/pages/admin-affairs/VehicleRequestListPage';
import SealRequestListPage from '@/pages/admin-affairs/SealRequestListPage';
import AnnouncementListPage from '@/pages/admin-affairs/AnnouncementListPage';
import AdminProcurementListPage from '@/pages/admin-procurement/AdminProcurementListPage';
import AdminItemMovementListPage from '@/pages/admin-procurement/AdminItemMovementListPage';
import AdminDashboardPage from '@/pages/admin-dashboard/AdminDashboardPage';
// Unified Scan — Issue #186
import UnifiedScanPage from '@/pages/scan/UnifiedScanPage';
// AI Model Configuration Center — Issue #187
import AiModelCenterPage from '@/pages/ai-model-center/AiModelCenterPage';
// SMS template / send-log admin — Issue #196
import SmsAdminPage from '@/pages/sms/SmsAdminPage';
// Reports center / Settings center — Issue #227
import ReportsPage from '@/pages/reports/ReportsPage';
// Sales funnel & channel ROI summary — Issue #397
import SalesFunnelChannelRoiSummaryPage from '@/pages/reports/SalesFunnelChannelRoiSummaryPage';
import SettingsPage from '@/pages/settings/SettingsPage';
// Inventory pages — Issue #228 (navigation/discoverability)
import InventoryLayout from '@/components/layouts/InventoryLayout';
import { ConsumableList } from '@/pages/inventory/ConsumableList';
import { InventoryMovements } from '@/pages/inventory/InventoryMovements';
// Intake assessment — Issue #325
import IntakeAssessmentListPage from '@/pages/intake-assessment/IntakeAssessmentListPage';
import IntakeAssessmentDetailPage from '@/pages/intake-assessment/IntakeAssessmentDetailPage';
import IntakeAssessmentCreatePage from '@/pages/intake-assessment/IntakeAssessmentCreatePage';
// Quote / Contract — Issue #327
import QuoteListPage from '@/pages/quote-contract/QuoteListPage';
import QuoteCreatePage from '@/pages/quote-contract/QuoteCreatePage';
import QuoteDetailPage from '@/pages/quote-contract/QuoteDetailPage';
import ContractListPage from '@/pages/quote-contract/ContractListPage';
import ContractDetailPage from '@/pages/quote-contract/ContractDetailPage';
import QuoteChangeListPage from '@/pages/quote-contract/QuoteChangeListPage';
import QuoteChangeCreatePage from '@/pages/quote-contract/QuoteChangeCreatePage';
import QuoteChangeDetailPage from '@/pages/quote-contract/QuoteChangeDetailPage';
// Procurement (service catalog / suppliers / purchase orders) — Issue #331
import ProcurementLayout from '@/components/layouts/ProcurementLayout';
import ServiceCatalogPage from '@/pages/procurement/ServiceCatalogPage';
import SuppliersPage from '@/pages/procurement/SuppliersPage';
import PurchaseOrdersPage from '@/pages/procurement/PurchaseOrdersPage';
import PurchaseOrderDetailPage from '@/pages/procurement/PurchaseOrderDetailPage';
// Follow-up plans / visits / outcome incidents / reports — Issue #333
import FollowUpLayout from '@/components/layouts/FollowUpLayout';
import FollowUpPlansPage from '@/pages/follow-up/FollowUpPlansPage';
import FollowUpPlanDetailPage from '@/pages/follow-up/FollowUpPlanDetailPage';
import FollowUpVisitsPage from '@/pages/follow-up/FollowUpVisitsPage';
import OutcomeIncidentsPage from '@/pages/follow-up/OutcomeIncidentsPage';
import FollowUpReportsPage from '@/pages/follow-up/FollowUpReportsPage';
import FollowUpReportDetailPage from '@/pages/follow-up/FollowUpReportDetailPage';

function ProtectedShell({ children }: { children: ReactNode }) {
  const {
    isAuthenticated,
    isInitializing,
    transientFailure,
    transientFailureReason,
    refreshSession,
  } = useAuth();
  const [collapsed, setCollapsed] = useState(false);

  if (isInitializing) {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">
        会话初始化中…
      </div>
    );
  }
  // Issue #230: a transient /auth/me failure must NOT redirect a previously
  // authenticated admin to /login. When we have no cached session and the
  // backend is flapping, render a retry panel instead of silently clearing
  // the session.
  if (transientFailure && !isAuthenticated) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center text-sm">
        <div className="text-base font-medium text-foreground">身份服务暂不可用</div>
        <div className="text-muted-foreground">
          后端身份接口暂时无法访问，请稍后重试或刷新页面。请勿重复登录。
        </div>
        {transientFailureReason ? (
          <div className="text-xs text-muted-foreground">原因码：{transientFailureReason}</div>
        ) : null}
        <button
          type="button"
          onClick={() => {
            void refreshSession();
          }}
          className="rounded-md border border-border px-4 py-2 text-sm hover:bg-muted"
        >
          重新尝试
        </button>
      </div>
    );
  }
  if (!isAuthenticated) {
    return <Redirect to="/login" />;
  }
  return (
    <DashboardLayout
      isSidebarCollapsed={collapsed}
      onToggleSidebar={() => setCollapsed((c) => !c)}
    >
      {children}
    </DashboardLayout>
  );
}

export default function App() {
  const authAdapter = useMemo(() => {
    // 生产构建：始终使用真实 adapter。
    // 开发构建：若设置了 VITE_API_BASE_URL，则使用真实 adapter（对接真实后端）；
    //             否则回落到 dev mock（仅开发模式且无后端时）。
    if (import.meta.env.PROD || import.meta.env.VITE_API_BASE_URL) {
      return createApiClientAuthAdapter({
        baseUrl: resolveApiBaseUrl(import.meta.env.VITE_API_BASE_URL),
      });
    }
    return undefined;
  }, []);

  return (
    <ErrorBoundary>
      <AuthProvider adapter={authAdapter}>
        <Toaster richColors position="top-right" />
        <Switch>
          <Route path="/login" component={LoginPage} />
          <Route path="/">
            <ProtectedShell>
              <DashboardPage />
            </ProtectedShell>
          </Route>

          {/* CRM routes */}
          <Route path="/crm/customers/create">
            <ProtectedShell>
              <CrmLayout>
                <CustomerCreatePage />
              </CrmLayout>
            </ProtectedShell>
          </Route>
          <Route path="/crm/customers/:customerId">
            <ProtectedShell>
              <CrmLayout>
                <CustomerDetailPage />
              </CrmLayout>
            </ProtectedShell>
          </Route>
          <Route path="/crm/customers">
            <ProtectedShell>
              <CrmLayout>
                <CustomerListPage />
              </CrmLayout>
            </ProtectedShell>
          </Route>
          <Route path="/crm/channel-partners/create">
            <ProtectedShell>
              <CrmLayout>
                <ChannelPartnerCreatePage />
              </CrmLayout>
            </ProtectedShell>
          </Route>
          <Route path="/crm/channel-partners/:partnerId">
            <ProtectedShell>
              <CrmLayout>
                <ChannelPartnerDetailPage />
              </CrmLayout>
            </ProtectedShell>
          </Route>
          <Route path="/crm/channel-partners">
            <ProtectedShell>
              <CrmLayout>
                <ChannelPartnerListPage />
              </CrmLayout>
            </ProtectedShell>
          </Route>
          {/* Channel ROI routes */}
          <Route path="/channel-roi/partners/:partnerId">
            <ProtectedShell>
              <CrmLayout>
                <ChannelRoiDetailPage />
              </CrmLayout>
            </ProtectedShell>
          </Route>
          <Route path="/channel-roi">
            <ProtectedShell>
              <CrmLayout>
                <ChannelRoiListPage />
              </CrmLayout>
            </ProtectedShell>
          </Route>
          {/* CRM Order routes */}
          <Route path="/crm/orders/create">
            <ProtectedShell>
              <CrmLayout>
                <CreateOrder />
              </CrmLayout>
            </ProtectedShell>
          </Route>
          <Route path="/crm/orders/:id">
            <ProtectedShell>
              <CrmLayout>
                <OrderDetail />
              </CrmLayout>
            </ProtectedShell>
          </Route>
          <Route path="/crm/orders">
            <ProtectedShell>
              <CrmLayout>
                <OrderList />

              </CrmLayout>
            </ProtectedShell>
          </Route>
          {/* Legacy /customers redirect to CRM */}
          <Route path="/customers">
            <Redirect to="/crm/customers" />
          </Route>

          {/* Reports center / Settings center — Issue #227 */}
          <Route path="/reports/sales-funnel-channel-roi-summary">
            <ProtectedShell>
              <SalesFunnelChannelRoiSummaryPage />
            </ProtectedShell>
          </Route>
          <Route path="/reports">
            <ProtectedShell>
              <ReportsPage />
            </ProtectedShell>
          </Route>
          <Route path="/settings">
            <ProtectedShell>
              <SettingsPage />
            </ProtectedShell>
          </Route>

          {/* Finance routes — Issue #134 */}
          <Route path="/finance/payment-records">
            <ProtectedShell>
              <FinanceLayout>
                <PaymentRecordsPage />
              </FinanceLayout>
            </ProtectedShell>
          </Route>
          <Route path="/finance/project-unlocks">
            <ProtectedShell>
              <FinanceLayout>
                <ProjectUnlocksPage />
              </FinanceLayout>
            </ProtectedShell>
          </Route>
          <Route path="/finance/revenue-ledgers">
            <ProtectedShell>
              <FinanceLayout>
                <RevenueLedgersPage />
              </FinanceLayout>
            </ProtectedShell>
          </Route>
          <Route path="/finance/commission-ledgers">
            <ProtectedShell>
              <FinanceLayout>
                <CommissionLedgersPage />
              </FinanceLayout>
            </ProtectedShell>
          </Route>
          {/* Finance routes — Issue #329 */}
          <Route path="/finance/invoices">
            <ProtectedShell>
              <FinanceLayout>
                <InvoicesPage />
              </FinanceLayout>
            </ProtectedShell>
          </Route>
          <Route path="/finance/installments">
            <ProtectedShell>
              <FinanceLayout>
                <InstallmentsPage />
              </FinanceLayout>
            </ProtectedShell>
          </Route>
          <Route path="/finance/adjustments">
            <ProtectedShell>
              <FinanceLayout>
                <AdjustmentsPage />
              </FinanceLayout>
            </ProtectedShell>
          </Route>
          {/* Finance routes — Issue #368 */}
          <Route path="/finance/templates">
            <ProtectedShell>
              <FinanceLayout>
                <FinanceTemplatesPage />
              </FinanceLayout>
            </ProtectedShell>
          </Route>
          {/* Finance routes — Issue #369 */}
          <Route path="/finance/counterparties">
            <ProtectedShell>
              <FinanceLayout>
                <FinanceCounterpartiesPage />
              </FinanceLayout>
            </ProtectedShell>
          </Route>
          {/* Finance routes — Issue #370 */}
          <Route path="/finance/payment-applications">
            <ProtectedShell>
              <FinanceLayout>
                <PaymentApplicationsPage />
              </FinanceLayout>
            </ProtectedShell>
          </Route>
          <Route path="/finance/employee-advances">
            <ProtectedShell>
              <FinanceLayout>
                <EmployeeAdvancesPage />
              </FinanceLayout>
            </ProtectedShell>
          </Route>
          <Route path="/finance/expense-reimbursements">
            <ProtectedShell>
              <FinanceLayout>
                <ExpenseReimbursementsPage />
              </FinanceLayout>
            </ProtectedShell>
          </Route>
          {/* Finance routes — Issue #371 */}
          <Route path="/finance/payroll/:id">
            <ProtectedShell>
              <FinanceLayout>
                <PayrollBatchDetailPage />
              </FinanceLayout>
            </ProtectedShell>
          </Route>
          <Route path="/finance/payroll">
            <ProtectedShell>
              <FinanceLayout>
                <PayrollBatchesPage />
              </FinanceLayout>
            </ProtectedShell>
          </Route>
          {/* Finance routes — Issue #372 */}
          <Route path="/finance/commission/rules">
            <ProtectedShell>
              <FinanceLayout>
                <CommissionRulesPage />
              </FinanceLayout>
            </ProtectedShell>
          </Route>
          <Route path="/finance/commission/details">
            <ProtectedShell>
              <FinanceLayout>
                <CommissionDetailsPage />
              </FinanceLayout>
            </ProtectedShell>
          </Route>
          <Route path="/finance/commission/payout-candidates">
            <ProtectedShell>
              <FinanceLayout>
                <CommissionPayoutCandidatesPage />
              </FinanceLayout>
            </ProtectedShell>
          </Route>
          <Route path="/finance/commission/export">
            <ProtectedShell>
              <FinanceLayout>
                <CommissionWideSheetExportPage />
              </FinanceLayout>
            </ProtectedShell>
          </Route>
          {/* Finance routes — Issue #373 */}
          <Route path="/finance/sales-report">
            <ProtectedShell>
              <FinanceLayout>
                <SalesReportPage />
              </FinanceLayout>
            </ProtectedShell>
          </Route>
          {/* Finance routes — Issue #402 */}
          <Route path="/finance/audit-view">
            <ProtectedShell>
              <FinanceLayout>
                <FinanceAuditViewPage />
              </FinanceLayout>
            </ProtectedShell>
          </Route>
          <Route path="/finance">
            <Redirect to="/finance/payment-records" />
          </Route>

          {/* Procurement routes — Issue #331 */}
          <Route path="/procurement/service-catalogs">
            <ProtectedShell>
              <ProcurementLayout>
                <ServiceCatalogPage />
              </ProcurementLayout>
            </ProtectedShell>
          </Route>
          <Route path="/procurement/suppliers">
            <ProtectedShell>
              <ProcurementLayout>
                <SuppliersPage />
              </ProcurementLayout>
            </ProtectedShell>
          </Route>
          <Route path="/procurement/purchase-orders/:id">
            <ProtectedShell>
              <ProcurementLayout>
                <PurchaseOrderDetailPage />
              </ProcurementLayout>
            </ProtectedShell>
          </Route>
          <Route path="/procurement/purchase-orders">
            <ProtectedShell>
              <ProcurementLayout>
                <PurchaseOrdersPage />
              </ProcurementLayout>
            </ProtectedShell>
          </Route>
          <Route path="/procurement">
            <Redirect to="/procurement/service-catalogs" />
          </Route>

          {/* Compliance routes */ }
          <Route path="/compliance/project-statuses">
            <ProtectedShell>
              <ComplianceLayout>
                <ProjectStatusesPage />
              </ComplianceLayout>
            </ProtectedShell>
          </Route>
          <Route path="/compliance/policy-files">
            <ProtectedShell>
              <ComplianceLayout>
                <PolicyFilesPage />
              </ComplianceLayout>
            </ProtectedShell>
          </Route>
          <Route path="/compliance/promotion-review">
            <ProtectedShell>
              <ComplianceLayout>
                <PromotionReviewPage />
              </ComplianceLayout>
            </ProtectedShell>
          </Route>
          <Route path="/compliance/sensitive-terms">
            <ProtectedShell>
              <ComplianceLayout>
                <SensitiveTermsPage />
              </ComplianceLayout>
            </ProtectedShell>
          </Route>
          <Route path="/compliance/recheck-queue">
            <ProtectedShell>
              <ComplianceLayout>
                <RecheckQueuePage />
              </ComplianceLayout>
            </ProtectedShell>
          </Route>

          {/* Task Center route — Issue #153 */}
          <Route path="/task-center">
            <ProtectedShell>
              <TaskCenterPage />
            </ProtectedShell>
          </Route>

          {/* Customer document review route — Issue #174 */}
          <Route path="/customer-documents">
            <ProtectedShell>
              <CustomerDocumentReviewPage />
            </ProtectedShell>
          </Route>

          {/* Reviewed health trend & AI report publication queue — Issue #177 */}
          <Route path="/health-report-publications">
            <ProtectedShell>
              <HealthReportPublicationReviewPage />
            </ProtectedShell>
          </Route>

          {/* HR routes — Issue #176 */}
          <Route path="/hr/employees">
            <ProtectedShell>
              <EmployeeListPage />
            </ProtectedShell>
          </Route>
          <Route path="/hr/imports/create">
            <ProtectedShell>
              <ImportCreatePage />
            </ProtectedShell>
          </Route>
          <Route path="/hr/imports/:id">
            <ProtectedShell>
              <ImportDetailPage />
            </ProtectedShell>
          </Route>
          <Route path="/hr/imports">
            <ProtectedShell>
              <ImportListPage />
            </ProtectedShell>
          </Route>

          {/* HR pages — Issue #188 */}
          <Route path="/hr/attendance/sync-runs">
            <ProtectedShell>
              <AttendanceSyncListPage />
            </ProtectedShell>
          </Route>
          <Route path="/hr/candidates/:id">
            <ProtectedShell>
              <CandidateDetailPage />
            </ProtectedShell>
          </Route>
          <Route path="/hr/candidates">
            <ProtectedShell>
              <CandidateListPage />
            </ProtectedShell>
          </Route>
          <Route path="/hr/interview-templates">
            <ProtectedShell>
              <InterviewTemplateListPage />
            </ProtectedShell>
          </Route>
          <Route path="/hr/interview-schedules">
            <ProtectedShell>
              <InterviewScheduleListPage />
            </ProtectedShell>
          </Route>
          <Route path="/hr/training-records">
            <ProtectedShell>
              <TrainingRecordListPage />
            </ProtectedShell>
          </Route>

          {/* HR credential / certificate routes — Issue #362 */}
          <Route path="/hr/certificates/create">
            <ProtectedShell>
              <CertificateCreatePage />
            </ProtectedShell>
          </Route>
          <Route path="/hr/certificates/:id">
            <ProtectedShell>
              <CertificateDetailPage />
            </ProtectedShell>
          </Route>
          <Route path="/hr/certificates">
            <ProtectedShell>
              <CertificateListPage />
            </ProtectedShell>
          </Route>

          {/* HR offboarding routes — Issue #363 */}
          <Route path="/hr/offboarding/create">
            <ProtectedShell>
              <OffboardingCreatePage />
            </ProtectedShell>
          </Route>
          <Route path="/hr/offboarding/:id">
            <ProtectedShell>
              <OffboardingDetailPage />
            </ProtectedShell>
          </Route>
          <Route path="/hr/offboarding">
            <ProtectedShell>
              <OffboardingListPage />
            </ProtectedShell>
          </Route>

          <Route path="/hr">
            <Redirect to="/hr/employees" />
          </Route>

          {/* Admin fixed assets — Issue #364 */}
          <Route path="/admin/assets/new">
            <ProtectedShell>
              <AdminAssetCreatePage />
            </ProtectedShell>
          </Route>
          <Route path="/admin/assets/:id">
            <ProtectedShell>
              <AdminAssetDetailPage />
            </ProtectedShell>
          </Route>
          <Route path="/admin/assets">
            <ProtectedShell>
              <AdminAssetListPage />
            </ProtectedShell>
          </Route>

          {/* Admin affairs — Issue #365 */}
          <Route path="/admin/affairs/meeting-rooms">
            <ProtectedShell>
              <MeetingRoomListPage />
            </ProtectedShell>
          </Route>
          <Route path="/admin/affairs/bookings">
            <ProtectedShell>
              <MeetingBookingListPage />
            </ProtectedShell>
          </Route>
          <Route path="/admin/affairs/vehicles">
            <ProtectedShell>
              <VehicleRequestListPage />
            </ProtectedShell>
          </Route>
          <Route path="/admin/affairs/seals">
            <ProtectedShell>
              <SealRequestListPage />
            </ProtectedShell>
          </Route>
          <Route path="/admin/affairs/announcements">
            <ProtectedShell>
              <AnnouncementListPage />
            </ProtectedShell>
          </Route>
          <Route path="/admin/affairs">
            <ProtectedShell>
              <AdminAffairsHubPage />
            </ProtectedShell>
          </Route>

          {/* Admin procurement + item movements + dashboard — Issue #366 */}
          <Route path="/admin/procurement/movements">
            <ProtectedShell>
              <AdminItemMovementListPage />
            </ProtectedShell>
          </Route>
          <Route path="/admin/procurement">
            <ProtectedShell>
              <AdminProcurementListPage />
            </ProtectedShell>
          </Route>
          <Route path="/admin/dashboard">
            <ProtectedShell>
              <AdminDashboardPage />
            </ProtectedShell>
          </Route>

          {/* Complaint / dispute work orders — Issue #175 */}
          <Route path="/complaints/create">
            <ProtectedShell>
              <ComplaintCreatePage />
            </ProtectedShell>
          </Route>
          <Route path="/complaints/:id">
            <ProtectedShell>
              <ComplaintDetailPage />
            </ProtectedShell>
          </Route>
          <Route path="/complaints">
            <ProtectedShell>
              <ComplaintListPage />
            </ProtectedShell>
          </Route>
          <Route path="/compliance">
            <ProtectedShell>
              <ComplianceLayout>
                <ComplianceHomePage />
              </ComplianceLayout>
            </ProtectedShell>
          </Route>

          {/* Scheduling routes */}
          <Route path="/scheduling/appointments/create">
            <ProtectedShell>
              <SchedulingLayout>
                <AppointmentCreatePage />
              </SchedulingLayout>
            </ProtectedShell>
          </Route>
          <Route path="/scheduling/appointments/:appointmentId">
            <ProtectedShell>
              <SchedulingLayout>
                <AppointmentDetailPage />
              </SchedulingLayout>
            </ProtectedShell>
          </Route>
          <Route path="/scheduling/appointments">
            <ProtectedShell>
              <SchedulingLayout>
                <AppointmentListPage />
              </SchedulingLayout>
            </ProtectedShell>
          </Route>
          <Route path="/scheduling/staff-schedules">
            <ProtectedShell>
              <SchedulingLayout>
                <StaffSchedulesPage />
              </SchedulingLayout>
            </ProtectedShell>
          </Route>

          {/*
            Issue #238: wouter v3 (regexparam under the hood) only matches
            `<Route path="/lab">` against the literal `/lab`, so direct visits
            to `/lab/batches` (or any deeper lab URL) previously fell through to
            the global 404. The trailing wildcard segment matches every nested
            lab subpath while preserving the shared `LabLayout` wrapper and the
            inner `<Switch>` that resolves each concrete lab route.
          */}
          <Route path="/lab/*">
            <ProtectedShell>
              <LabLayout>
                <Switch>
                  <Route path="/lab/batches" component={CellBatchesListPage} />
                  <Route path="/lab/batches/:batchId" component={CellBatchDetailPage} />
                  <Route path="/lab/qc" component={QcOrdersListPage} />
                  <Route path="/lab/qc/:qcOrderId" component={QcOrderDetailPage} />
                  <Route path="/lab/release" component={ReleaseOrdersListPage} />
                  <Route path="/lab/release/:releaseOrderId" component={ReleaseOrderDetailPage} />
                  <Route path="/lab/infusion" component={InfusionOrdersListPage} />
                  <Route path="/lab/infusion/:infusionId" component={InfusionOrderDetailPage} />
                  <Route path="/lab/infusion/:infusionId/verify" component={InfusionIdentityVerifyPage} />
                  <Route path="/lab/exceptions" component={ExceptionsListPage} />
                  <Route path="/lab/exceptions/:eventId" component={ExceptionEventDetailPage} />
                  <Route path="/lab/sample-records" component={SampleRecordsListPage} />
                  <Route path="/lab/sample-records/create" component={SampleRecordCreatePage} />
                  <Route path="/lab/sample-records/:recordId" component={SampleRecordDetailPage} />
                  <Route path="/lab/traceability" component={LabTraceabilityPage} />
                  <Route component={NotFoundPage} />
                </Switch>
              </LabLayout>
            </ProtectedShell>
          </Route>
          {/* Unified scan — Issue #186 */}
          <Route path="/scan">
            <ProtectedShell>
              <UnifiedScanPage />
            </ProtectedShell>
          </Route>
          {/* AI Model Configuration Center — Issue #187 */}
          <Route path="/ai-model-center">
            <ProtectedShell>
              <AiModelCenterPage />
            </ProtectedShell>
          </Route>
          {/* SMS template / send-log admin — Issue #196 */}
          <Route path="/sms">
            <ProtectedShell>
              <SmsAdminPage />
            </ProtectedShell>
          </Route>
          {/* Operations routes */}
          <Route path="/operations/work-orders/create">
            <ProtectedShell>
              <WorkOrderCreatePage />
            </ProtectedShell>
          </Route>
          <Route path="/operations/work-orders/:id">
            <ProtectedShell>
              <WorkOrderDetailPage />
            </ProtectedShell>
          </Route>
          <Route path="/operations/work-orders">
            <ProtectedShell>
              <WorkOrderListPage />
            </ProtectedShell>
          </Route>
          {/* Inventory routes — Issue #228 */}
          <Route path="/inventory/consumables">
            <ProtectedShell>
              <InventoryLayout>
                <ConsumableList />
              </InventoryLayout>
            </ProtectedShell>
          </Route>
          <Route path="/inventory/movements">
            <ProtectedShell>
              <InventoryLayout>
                <InventoryMovements />
              </InventoryLayout>
            </ProtectedShell>
          </Route>
          <Route path="/inventory">
            <Redirect to="/inventory/consumables" />
          </Route>

          {/* Risk read-model (follow-up / complaints / compliance) — Issue #400 */}
          <Route path="/risk/read-model">
            <ProtectedShell>
              <RiskReadModelPage />
            </ProtectedShell>
          </Route>
          <Route path="/risk">
            <Redirect to="/risk/read-model" />
          </Route>

          {/* Follow-up plans / visits / outcome incidents / reports — Issue #333 */}
          <Route path="/follow-up/plans/:id">
            <ProtectedShell>
              <FollowUpLayout>
                <FollowUpPlanDetailPage />
              </FollowUpLayout>
            </ProtectedShell>
          </Route>
          <Route path="/follow-up/plans">
            <ProtectedShell>
              <FollowUpLayout>
                <FollowUpPlansPage />
              </FollowUpLayout>
            </ProtectedShell>
          </Route>
          <Route path="/follow-up/visits">
            <ProtectedShell>
              <FollowUpLayout>
                <FollowUpVisitsPage />
              </FollowUpLayout>
            </ProtectedShell>
          </Route>
          <Route path="/follow-up/outcome-incidents">
            <ProtectedShell>
              <FollowUpLayout>
                <OutcomeIncidentsPage />
              </FollowUpLayout>
            </ProtectedShell>
          </Route>
          <Route path="/follow-up/reports/:id">
            <ProtectedShell>
              <FollowUpLayout>
                <FollowUpReportDetailPage />
              </FollowUpLayout>
            </ProtectedShell>
          </Route>
          <Route path="/follow-up/reports">
            <ProtectedShell>
              <FollowUpLayout>
                <FollowUpReportsPage />
              </FollowUpLayout>
            </ProtectedShell>
          </Route>
          <Route path="/follow-up">
            <Redirect to="/follow-up/plans" />
          </Route>

          {/* Intake assessment routes — Issue #325 */}
          <Route path="/intake-assessment/records/create">
            <ProtectedShell>
              <IntakeAssessmentCreatePage />
            </ProtectedShell>
          </Route>
          <Route path="/intake-assessment/records/:id">
            <ProtectedShell>
              <IntakeAssessmentDetailPage />
            </ProtectedShell>
          </Route>
          <Route path="/intake-assessment/records">
            <ProtectedShell>
              <IntakeAssessmentListPage />
            </ProtectedShell>
          </Route>
          <Route path="/intake-assessment">
            <Redirect to="/intake-assessment/records" />
          </Route>

          {/* Proposal routes */}
          <Route path="/proposals/create">
            <ProtectedShell>
              <ProposalLayout>
                <ProposalCreatePage />
              </ProposalLayout>
            </ProtectedShell>
          </Route>
          {/* AI proposal review dashboard — Issue #394.
              Registered BEFORE `/proposals/:proposalId` so the static path
              isn't shadowed by the dynamic param. */}
          <Route path="/proposals/ai-review-dashboard">
            <ProtectedShell>
              <ProposalLayout>
                <AiProposalReviewDashboardPage />
              </ProposalLayout>
            </ProtectedShell>
          </Route>
          <Route path="/proposals/:proposalId">
            <ProtectedShell>
              <ProposalLayout>
                <ProposalDetailPage />
              </ProposalLayout>
            </ProtectedShell>
          </Route>
          <Route path="/proposals">
            <ProtectedShell>
              <ProposalLayout>
                <ProposalListPage />
              </ProposalLayout>
            </ProtectedShell>
          </Route>

          {/* Quote / Contract routes — Issue #327 */}
          <Route path="/quote-contract/quotes/create">
            <ProtectedShell>
              <QuoteCreatePage />
            </ProtectedShell>
          </Route>
          <Route path="/quote-contract/quotes/:id">
            <ProtectedShell>
              <QuoteDetailPage />
            </ProtectedShell>
          </Route>
          <Route path="/quote-contract/quotes">
            <ProtectedShell>
              <QuoteListPage />
            </ProtectedShell>
          </Route>
          <Route path="/quote-contract/contracts/:id">
            <ProtectedShell>
              <ContractDetailPage />
            </ProtectedShell>
          </Route>
          <Route path="/quote-contract/contracts">
            <ProtectedShell>
              <ContractListPage />
            </ProtectedShell>
          </Route>
          <Route path="/quote-contract/change-requests/create">
            <ProtectedShell>
              <QuoteChangeCreatePage />
            </ProtectedShell>
          </Route>
          <Route path="/quote-contract/change-requests/:id">
            <ProtectedShell>
              <QuoteChangeDetailPage />
            </ProtectedShell>
          </Route>
          <Route path="/quote-contract/change-requests">
            <ProtectedShell>
              <QuoteChangeListPage />
            </ProtectedShell>
          </Route>
          <Route path="/quote-contract">
            <Redirect to="/quote-contract/quotes" />
          </Route>

          <Route component={NotFoundPage} />
        </Switch>
      </AuthProvider>
    </ErrorBoundary>
  );
}
